import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import { type Product, useUpdateProduct } from '@/models/product';
import {
  EditButton,
  SaveCancelButtons,
} from '@/routes/SettingsBeta/components/SaveEditCancelButtons';

import { ExternalId } from './ExternalId';

export const ExternalIdSection = ({ product }: { product: Product }) => {
  const { t } = useLingui();
  const { mutateAsync: updateProduct, isPending: isUpdateProductPending } = useUpdateProduct(
    product.id
  );
  const [isEditing, setIsEditing] = useState(false);
  const [externalId, setExternalId] = useState<string | null>(product.externalId);
  const [showErrors, setShowErrors] = useState(false);

  const missingExternalId = !externalId?.trim();

  const handleSave = async () => {
    const hasError = missingExternalId;

    if (hasError) {
      setShowErrors(true);

      return;
    }

    setShowErrors(false);
    setIsEditing(false);

    await updateProduct({ externalId: externalId!.trim() });
  };

  const handleCancel = () => {
    setExternalId(product.externalId);
    setShowErrors(false);
    setIsEditing(false);
  };

  return (
    <Box>
      <Stack gap="0.5rem">
        <Stack row justifyContent="space-between">
          <ExternalId
            product={product}
            isEditing={isEditing}
            externalId={externalId}
            setExternalId={setExternalId}
            isInvalid={showErrors && missingExternalId}
          />

          {isEditing ? (
            <SaveCancelButtons
              onSave={handleSave}
              onCancel={handleCancel}
              isSaving={isUpdateProductPending}
            />
          ) : (
            <EditButton
              label={t({
                id: 'settings.organization.data-catalogs.products.product.external-id-section.edit',
                message: 'Edit product internal id',
              })}
              onClick={() => setIsEditing(true)}
            />
          )}
        </Stack>

        {showErrors && (
          <Message type="error">
            <Trans id="settings.organization.data-catalogs.products.product.errors.external-id.missing">
              An internal product identifier is required
            </Trans>
          </Message>
        )}
      </Stack>
    </Box>
  );
};
